import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { keyBy } from "lodash";

export function useFormFieldController({
  name,
  parameters,
  operand,
  value,
  datePart,
  formField,
}) {
  const formFieldsMap = useMemo(() => {
    return keyBy(parameters, "name");
  }, [parameters]);

  const getDefaultValue = (
    formFieldValue,
    datePartValue,
    fieldOperand = ""
  ) => {
    if (
      datePartValue === "Month" ||
      datePartValue === "Weekday" ||
      formFieldsMap[formFieldValue]?.type.id === "MULTISELECT_DROPDOWN" ||
      (formFieldsMap[formFieldValue]?.type.id === "DROPDOWN" &&
        fieldOperand === "is any of")
    ) {
      return [];
    } else if (formFieldsMap[formFieldValue]?.type.id === "DROPDOWN") {
      return null;
    } else {
      return "";
    }
  };

  const getFormFieldDateParts = (formFieldValue, datePartValue) => {
    return formFieldsMap[formFieldValue]?.dateParts.filter((dp) => {
      return dp.name === datePartValue;
    })[0];
  };

  const formFieldHasDatePart = (formFieldValue) => {
    return Boolean(formFieldsMap[formFieldValue]?.dateParts);
  };

  const getDefaultDatePart = (formFieldValue) => {
    if (!formFieldValue) return "";

    return formFieldHasDatePart(formFieldValue)
      ? formFieldsMap[formFieldValue]?.dateParts[0].name
      : null;
  };

  const getDefaultOperand = (formFieldValue) => {
    if (!formFieldValue) return "";

    return formFieldHasDatePart(formFieldValue)
      ? formFieldsMap[formFieldValue]?.dateParts[0]?.operands[0]
      : formFieldsMap[formFieldValue]?.operands[0];
  };

  const getOptionsSource = (formFieldValue, datePartValue) => {
    return datePartValue
      ? getFormFieldDateParts(formFieldValue, datePartValue)
      : formFieldsMap[formFieldValue];
  };

  const getFieldType = (formFieldValue, datePartValue, fieldOperand = "") => {
    if (datePartValue) {
      return getFormFieldDateParts(formFieldValue, datePartValue).type;
    } else if (formFieldsMap[formFieldValue]?.type.id === "CHECKBOX") {
      return null;
    } else if (
      formFieldsMap[formFieldValue]?.type.id === "DROPDOWN" &&
      fieldOperand === "is any of"
    ) {
      return {
        id: "MULTISELECT_DROPDOWN",
        showOptions: true,
        text: "Multiselect Dropdown",
      };
    } else {
      return formFieldsMap[formFieldValue]?.type;
    }
  };

  const { field } = useController({
    name: name,
    defaultValue: {
      formFieldName: formField?.name || null,
      formFieldId: formField?.id || null,
      datePart: datePart || getDefaultDatePart(formField?.name),
      operand: operand || getDefaultOperand(formField?.name),
    },
    rules: {
      validate: ({ formFieldName: ff, datePart: d, operand: o }) => {
        return formFieldHasDatePart(formField)
          ? Boolean(ff && d && o)
          : Boolean(ff && o);
      },
      required: {
        value: true,
        message: "Please select form field parameters",
      },
    },
  });

  const { resetField, getValues, setValue } = useFormContext();

  const checkValueStillApplicable = (
    formFieldValue,
    newOperand,
    oldOperand
  ) => {
    const newOperandIsAnyOf = newOperand === "is any of";
    const oldOperandIsAnyOf = oldOperand === "is any of";
    if (!newOperandIsAnyOf && !oldOperandIsAnyOf) {
      return;
    }

    const fieldName = `${name}.value`;
    const currentValue = getValues(fieldName);

    if (
      Array.isArray(currentValue) &&
      currentValue.length === 1 &&
      oldOperandIsAnyOf
    ) {
      setValue(fieldName, currentValue[0]);
    } else if (typeof currentValue === "string" && newOperandIsAnyOf) {
      setValue(fieldName, [currentValue]);
    } else if (formFieldsMap[formFieldValue]?.type.id === "DROPDOWN") {
      // The following addition will reset the value if switching
      // from or to the operand "is any of"
      resetField(`${name}.value`, {
        defaultValue: getDefaultValue(formFieldValue, null, newOperand),
      });
    }
  };

  return {
    formFieldProps: {
      options: Object.keys(formFieldsMap),
      value: field.value?.formFieldName || null,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          formFieldName: formFieldsMap[v].name,
          formFieldId: formFieldsMap[v].id,
          datePart: getDefaultDatePart(v),
          operand: getDefaultOperand(v),
        });

        resetField(`${name}.value`, {
          defaultValue: getDefaultValue(
            v,
            formFieldHasDatePart(v) ? formFieldsMap[v]?.dateParts[0] : null,
            field.value?.operand
          ),
        });
      },
    },
    datePartProps: {
      options: formFieldsMap[field.value?.formFieldName]?.dateParts?.map(
        (dp) => {
          return dp.name;
        }
      ),
      value: field.value?.datePart,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          datePart: v,
          operand: getFormFieldDateParts(field.value?.formFieldName, v)
            .operands[0],
        });

        resetField(`${name}.value`, {
          defaultValue: getDefaultValue(
            field.value?.formFieldName,
            v,
            field.value?.operand
          ),
        });
      },
    },
    operandProps: {
      options: getOptionsSource(
        field.value?.formFieldName,
        field.value?.datePart
      )?.operands,
      value: field.value?.operand,
      onBlur: field.onBlur,
      onChange: (_, v) => {
        field.onChange({
          ...field.value,
          value: getValues(`${name}.value`),
          operand: v,
        });

        checkValueStillApplicable(
          field.value?.formFieldName,
          v,
          field.value?.operand
        );
      },
    },
    fieldProps: {
      id: `${name}.value`,
      name: "",
      options: getOptionsSource(
        field.value?.formFieldName,
        field.value?.datePart
      )?.options,
      type: getFieldType(
        field.value?.formFieldName,
        field.value?.datePart,
        field.value?.operand
      ),
      value:
        value ||
        getDefaultValue(
          field.value?.formFieldName,
          field.value?.datePart,
          field.value?.operand
        ),
    },
    hasDatePart: Boolean(field.value?.datePart),
    hasOperand: Boolean(field.value?.operand),
    hasField: Boolean(
      getFieldType(
        field.value?.formFieldName,
        field.value?.dataPart,
        field.value?.operand
      )
    ),
  };
}

export default useFormFieldController;
